import React from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider } from './context/AuthContext';
import { ScenarioProvider } from './context/ScenarioContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import PageErrorBoundary from './components/PageErrorBoundary';
import SignIn from './pages/SignIn';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import Scenarios from './pages/Scenarios';
import Assumptions from './pages/Assumptions';
import PnL from './pages/PnL';
import MarginAnalysis from './pages/MarginAnalysis';
import CashFlow from './pages/CashFlow';
import BHAG from './pages/BHAG';
import DailyPulse from './pages/DailyPulse';
import TeamRoster from './pages/TeamRoster';
import StoryMode from './pages/StoryMode';
import AuditLog from './pages/AuditLog';
import Alerts from './pages/Alerts';
import Integrations from './pages/Integrations';
import InvestmentAnalyzer from './pages/InvestmentAnalyzer';
import ExpenseAudit from './pages/ExpenseAudit';
import MarketingROI from './pages/MarketingROI';
import OwnersPay from './pages/OwnersPay';
import SalesDashboard from './pages/sales/SalesDashboard';
import SalesRearview from './pages/sales/SalesRearview';
import SalesConsole from './pages/sales/SalesConsole';
import SalesPlan from './pages/sales/SalesPlan';
import SalesIntegrity from './pages/sales/SalesIntegrity';
import ProfitFirst from './pages/ProfitFirst';
import BudgetVsActuals from './pages/BudgetVsActuals';
import './App.css';

const Protected = ({ children }) => (
  <ProtectedRoute>
    <ScenarioProvider>
      <Layout>
        <PageErrorBoundary>{children}</PageErrorBoundary>
      </Layout>
    </ScenarioProvider>
  </ProtectedRoute>
);

// AppRouter — synchronously checks the URL fragment for an Emergent Auth
// session_id BEFORE the normal Routes render. This prevents a race where
// the AuthProvider's /auth/me check runs before the callback exchange.
// Reading from useLocation().hash (not window.location.hash) is required
// so React re-renders when history.replaceState clears the fragment.
function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/signin" element={<SignIn />} />
      <Route path="/" element={<Protected><Dashboard /></Protected>} />
      {/* Phase 2 · scenario picker retired. Legacy /scenarios and
          /assumptions redirect into the new one-scenario paths. */}
      <Route path="/admin/scenarios" element={<Protected><Scenarios /></Protected>} />
      <Route path="/scenarios" element={<Navigate to="/admin/scenarios" replace />} />
      <Route path="/plan-inputs" element={<Protected><Assumptions /></Protected>} />
      <Route path="/assumptions" element={<Navigate to="/plan-inputs" replace />} />
      <Route path="/pnl" element={<Protected><PnL /></Protected>} />
      <Route path="/budget-vs-actuals" element={<Protected><BudgetVsActuals /></Protected>} />
      <Route path="/margin-analysis" element={<Protected><MarginAnalysis /></Protected>} />
      <Route path="/cashflow" element={<Protected><CashFlow /></Protected>} />
      <Route path="/bhag" element={<Protected><BHAG /></Protected>} />
      <Route path="/pulse" element={<Protected><DailyPulse /></Protected>} />
      <Route path="/team" element={<Protected><TeamRoster /></Protected>} />
      <Route path="/sales-legacy" element={<Navigate to="/sales/dashboard" replace />} />
      <Route path="/sales" element={<Navigate to="/sales/dashboard" replace />} />
      <Route path="/sales/dashboard" element={<Protected><SalesDashboard /></Protected>} />
      <Route path="/sales/rearview" element={<Protected><SalesRearview /></Protected>} />
      <Route path="/sales/console" element={<Protected><SalesConsole /></Protected>} />
      <Route path="/sales/plan" element={<Protected><SalesPlan /></Protected>} />
      <Route path="/sales/integrity" element={<Protected><SalesIntegrity /></Protected>} />
      <Route path="/profit-first" element={<Protected><ProfitFirst /></Protected>} />
      <Route path="/investments" element={<Protected><InvestmentAnalyzer /></Protected>} />
      <Route path="/expense-audit" element={<Protected><ExpenseAudit /></Protected>} />
      <Route path="/marketing-roi" element={<Protected><MarketingROI /></Protected>} />
      <Route path="/owners-pay" element={<Protected><OwnersPay /></Protected>} />
      <Route path="/story" element={<Protected><StoryMode /></Protected>} />
      <Route path="/audit" element={<Protected><AuditLog /></Protected>} />
      <Route path="/alerts" element={<Protected><Alerts /></Protected>} />
      <Route path="/integrations" element={<Protected><Integrations /></Protected>} />
      {/* Any unknown path (including the removed /register) → home so we
          never leave the user on a blank screen. Protected route bounces
          unauthenticated users to /signin. */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster position="top-right" richColors theme="light" />
    </AuthProvider>
  );
}

export default App;
